import {
    DecoratorNode,
    DOMConversionMap,
    DOMExportOutput,
    LexicalNode,
    NodeKey,
    SerializedLexicalNode,
    Spread,
} from 'lexical'
import { ReactNode } from 'react'
import { VariableIcon, RawVariableIcon } from '../icons'
import { pathToLabel } from './variableUtils'

export type SerializedVariableNode = Spread<{
    path: string
    label: string
    raw?: boolean
}, SerializedLexicalNode>

export class VariableNode extends DecoratorNode<ReactNode> {
    __path: string
    __label: string
    __raw: boolean

    static getType(): string {
        return 'variable'
    }

    static clone(node: VariableNode): VariableNode {
        return new VariableNode(node.__path, node.__label, node.__raw, node.__key)
    }

    constructor(path: string, label?: string, raw = false, key?: NodeKey) {
        super(key)
        this.__path = path
        this.__label = label ?? pathToLabel(path)
        this.__raw = raw
    }

    createDOM(): HTMLElement {
        const span = document.createElement('span')
        span.className = 'variable-pill'
        return span
    }

    updateDOM(): boolean {
        return false
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement('span')
        element.textContent = this.__raw
            ? `{{{${this.__path}}}}`
            : `{{${this.__path}}}`
        return { element }
    }

    static importDOM(): DOMConversionMap | null {
        return null
    }

    static importJSON(serializedNode: SerializedVariableNode): VariableNode {
        return $createVariableNode(serializedNode.path, serializedNode.label, serializedNode.raw)
    }

    exportJSON(): SerializedVariableNode {
        return {
            type: 'variable',
            version: 1,
            path: this.__path,
            label: this.__label,
            raw: this.__raw || undefined,
        }
    }

    getTextContent(): string {
        return this.__raw
            ? `{{{${this.__path}}}}`
            : `{{${this.__path}}}`
    }

    isInline(): boolean {
        return true
    }

    decorate(): ReactNode {
        return <span className="pill-content variable-pill-content">
            {this.__raw ? <RawVariableIcon /> : <VariableIcon />}
            {this.__label}
        </span>
    }
}

export const $createVariableNode = (path: string, label?: string, raw = false): VariableNode => {
    return new VariableNode(path, label, raw)
}

export const $isVariableNode = (node: LexicalNode | null | undefined): node is VariableNode => {
    return node instanceof VariableNode
}
